import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { addToast } from '../../../../../store/toast/toastSlice'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetIntegrationEntitiesQuery } from '../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { type MultiSelectOption } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import {
  type WealthboxAttendeeSource,
  type WealthboxInboundContactSyncSettingsFilters,
  type WealthboxSettingsFormValues,
  toWealthboxAttendeeSource,
} from './models'

export const useWealthboxContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'wealthbox',
    direction: 'inbound',
    object: 'contact',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [serviceConfig, setServiceConfig] = useState<{ [key: string]: any } | null>(null)
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  const { data: tags, isFetching: isTagsLoading } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.WEALTHBOX,
    entityType: 'contact-tags',
  })

  const [tierTagSelectOptions, setTierTagSelectOptions] = useState<MultiSelectOption[] | null>(null)

  useEffect(() => {
    if (!tags || !tags.data || !tags.data.tags || tags.data.tags.length === 0) {
      setTierTagSelectOptions([])
      return
    }
    const options = tags.data.tags.map((tag) => ({ id: tag.id, label: tag.name }))
    setTierTagSelectOptions(options)
  }, [tags])

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'wealthbox')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    if (userData) {
      const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
      if (integration) {
        setServiceConfig(integration.serviceConfig ?? null)
      }
    }
  }, [userData])

  const save = async (data: WealthboxSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
      if (integration) {
        const filters: WealthboxInboundContactSyncSettingsFilters = {
          active: data.inbound.contact.filters.active ?? undefined,
          contactType: data.inbound.contact.filters.contactType || undefined,
          tags: (data.inbound.contact.filters.tags ?? []).length > 0 ? data.inbound.contact.filters.tags : undefined,
          type: data.inbound.contact.filters.type || undefined,
        }

        const tierTags = (data.inbound.contact.tierTags ?? []).length > 0 ? data.inbound.contact.tierTags : null

        const mergedServiceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters, tierTags } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(mergedServiceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (autoSync == null) {
        if (active) await createCheckpoint(checkpointRequest).unwrap()
      } else {
        await updateCheckpoint({ ...checkpointRequest, active }).unwrap()
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync,
    serviceConfig,
    tierTagSelectOptions,
    isSyncDisabled,
    isAccountFetching,
    isFetching: !isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching || isTagsLoading),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
    saveAutoSync,
  }
}

export const useWealthboxCalendarSync = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'wealthbox',
    direction: 'inbound',
    object: 'calendar',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const { data: me, isFetching: isGetMeFetching, isError: isGetMeError } = useGetMeQuery({ accountId })
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()

  const integration = me?.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
  const attendeeSource = integration ? toWealthboxAttendeeSource(integration.serviceConfig?.inbound?.calendar?.attendeeSource) : null

  useEffect(() => {
    if (isGetMeError) {
      dispatch(
        addToast({
          variant: 'error',
          title: "Couldn't load meeting attendee settings",
          messages: ['Your Wealthbox connection details did not load. Reload the page to try again.'],
        }),
      )
    }
  }, [isGetMeError, dispatch])

  const save = async (enabled: boolean, nextAttendeeSource?: WealthboxAttendeeSource): Promise<boolean> => {
    try {
      setIsUpdating(true)
      if (nextAttendeeSource && !integration) {
        throw new Error('Cannot save the attendee source without a loaded Wealthbox integration')
      }
      if (!checkpoint?.data) {
        if (enabled) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: enabled }).unwrap()
      }
      if (integration && nextAttendeeSource && nextAttendeeSource !== attendeeSource) {
        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: merge(integration.serviceConfig || {}, { inbound: { calendar: { attendeeSource: nextAttendeeSource } } }) },
        }).unwrap()
      }
      return true
    } catch (e) {
      console.error(e)
      dispatch(addToast({ variant: 'error', title: 'Saving failed', messages: ['Your calendar sync setting was not saved. Please try again.'] }))
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  return {
    autoSync: checkpoint?.data?.active ?? false,
    attendeeSource,
    checkpoint,
    isGetCheckpointFetching,
    isGetMeFetching,
    isFetching: !isUpdating && (isGetCheckpointFetching || isGetMeFetching),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
  }
}
